import { PUBLIC_DOCUMENT_LOCALES } from "./ssr/public-document";
export const PUBLIC_BROWSE_SERVICE_WORKER_PATH = "/public-browse-sw.js";
export const PUBLIC_BROWSE_CACHE_PREFIX = "glider-public-browse-v1:";
export const PUBLIC_BROWSE_CACHE_INVALIDATION_MESSAGE =
  "glider-public-browse-invalidate";

export function createPublicBrowseServiceWorkerScript(
  releaseSha: string,
): string {
  return String.raw`"use strict";
const RELEASE = ${JSON.stringify(releaseSha)};
const CACHE_PREFIX = ${JSON.stringify(PUBLIC_BROWSE_CACHE_PREFIX)};
const CACHE_NAME = CACHE_PREFIX + RELEASE;
const EXPIRES_HEADER = "X-Glider-Public-Cache-Until";
const MAX_AGE_MS = 30000;
const HOME_MAX_AGE_MS = 300000;
const MAX_ENTRIES = 32;
const LOCALES = ${JSON.stringify(PUBLIC_DOCUMENT_LOCALES)};
function isHomePath(pathname) {
  return pathname === "/" || LOCALES.some(locale => locale !== "en" && pathname === "/" + locale);
}
function isPublicPath(pathname) {
  if (isHomePath(pathname)) return true;
  const parts = pathname.split("/").slice(1);
  if (LOCALES.includes(parts[0])) { if (parts.shift() === "en") return false; }
  return (parts.length === 1 && ["explore", "stocks", "currencies", "build"].includes(parts[0])) ||
    (parts.length === 2 && parts[0] === "stocks" && /^[A-Z][A-Z0-9.-]{0,15}$/.test(parts[1]));
}
let cachingEnabled = true;

function canHandle(request) {
  const url = new URL(request.url);
  return request.method === "GET" && request.mode === "navigate" &&
    url.origin === self.location.origin && !url.search &&
    isPublicPath(url.pathname) &&
    !request.headers.has("authorization") &&
    !["reload", "no-cache", "no-store"].includes(request.cache) &&
    !/no-cache|no-store/i.test(request.headers.get("cache-control") || "");
}

function canCache(response, request) {
  const control = response.headers.get("cache-control") || "";
  const vary = response.headers.get("vary") || "";
  const snapshotExpiresAt = Number(response.headers.get("X-Public-SSR-Expires-At"));
  const home = isHomePath(new URL(request.url).pathname);
  // Browsers filter Set-Cookie. The server's public document marker asserts
  // that this is anonymous HTML without cookies or private state.
  return response.status === 200 && !response.redirected &&
    ["basic", "default"].includes(response.type) &&
    (home ? response.headers.get("X-Public-SSR-Document") === "static-home" :
      response.headers.get("X-Public-SSR-Snapshot") === "hit") &&
    response.headers.get("X-Glider-Release") === RELEASE &&
    (home || (Number.isSafeInteger(snapshotExpiresAt) && snapshotExpiresAt > Date.now())) &&
    !response.headers.has("set-cookie") &&
    /^text\/html(?:;|$)/i.test(response.headers.get("content-type") || "") &&
    /(?:^|,)\s*public\s*(?:,|$)/i.test(control) &&
    !/(?:^|,)\s*(?:private|no-store)(?:\s*=|\s*,|\s*$)/i.test(control) &&
    !/(?:^|,)\s*(?:\*|cookie|authorization)\s*(?:,|$)/i.test(vary);
}

async function storeResponse(cache, request, response) {
  const responseRelease = response.headers.get("X-Glider-Release");
  if (responseRelease && responseRelease !== RELEASE) {
    cachingEnabled = false;
    await caches.delete(CACHE_NAME);
    await self.registration.update().catch(() => undefined);
    return;
  }
  if (!cachingEnabled || !canCache(response, request)) {
    await cache.delete(request);
    return;
  }
  const now = Date.now();
  const age = Math.max(0, Number(response.headers.get("age")) || 0) * 1000;
  const responseDate = Date.parse(response.headers.get("date") || "");
  const home = isHomePath(new URL(request.url).pathname);
  const maxAge = home ? HOME_MAX_AGE_MS : MAX_AGE_MS;
  // Static asset Age describes unchanged bytes, not market freshness. The
  // delivery Worker has just validated that those bytes match its release.
  const remaining = home ? maxAge : Math.min(
    Number(response.headers.get("X-Public-SSR-Expires-At")) - now,
    maxAge - Math.max(age, Number.isFinite(responseDate) ? now - responseDate : 0)
  );
  if (remaining <= 0) {
    await cache.delete(request);
    return;
  }
  const headers = new Headers(response.headers);
  headers.set(EXPIRES_HEADER, String(now + remaining));
  await cache.put(request, new Response(response.body, { status: 200, headers }));
  const entries = await cache.keys();
  await Promise.all(entries.slice(0, Math.max(0, entries.length - MAX_ENTRIES)).map(key => cache.delete(key)));
  // An invalidation may arrive while Cache.put consumes the streamed body.
  if (!cachingEnabled) await cache.delete(request);
}

async function handleNavigation(request, background) {
  let cache;
  try { cache = await caches.open(CACHE_NAME); }
  catch { return fetch(request, { cache: "no-store" }); }

  const refresh = async (waitForStorage = false) => {
    const response = await fetch(request, { cache: "no-store" });
    const storage = storeResponse(cache, request, response.clone()).catch(() => undefined);
    if (waitForStorage) await storage;
    else background.push(storage);
    return response;
  };
  let cached;
  try { cached = cachingEnabled ? await cache.match(request) : undefined; }
  catch { /* Storage failures must not prevent a network navigation. */ }
  const expiry = Number(cached?.headers.get(EXPIRES_HEADER));
  const now = Date.now();
  const maxAge = isHomePath(new URL(request.url).pathname) ? HOME_MAX_AGE_MS : MAX_AGE_MS;
  if (cached && canCache(cached, request) && expiry > now && expiry <= now + maxAge) {
    background.push(refresh(true).catch(() => undefined));
    const headers = new Headers(cached.headers);
    headers.set("X-Public-SSR-Client-Cache", "hit");
    return new Response(cached.body, { status: 200, headers });
  }
  return refresh();
}

self.addEventListener("install", (event) => {
  event.waitUntil(self.skipWaiting());
});
self.addEventListener("activate", (event) => {
  event.waitUntil((async () => {
    const names = await caches.keys();
    await Promise.all(names.filter(name => name.startsWith(CACHE_PREFIX) && name !== CACHE_NAME).map(name => caches.delete(name)));
    await self.clients.claim();
  })());
});
self.addEventListener("message", (event) => {
  if (event.data?.type !== ${JSON.stringify(PUBLIC_BROWSE_CACHE_INVALIDATION_MESSAGE)}) return;
  cachingEnabled = false;
  event.waitUntil(caches.delete(CACHE_NAME).catch(() => false).then(() => {
    event.ports[0]?.postMessage({ cleared: true });
    return self.registration.update().catch(() => undefined);
  }));
});
self.addEventListener("fetch", (event) => {
  if (!canHandle(event.request)) return;
  const background = [];
  const response = handleNavigation(event.request, background);
  event.respondWith(response);
  event.waitUntil(response.then(() => Promise.allSettled(background), () => undefined));
});
`;
}
